import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { useGetBots, useGetBotDetail, invalidateBots } from 'actions/chatbot';
import LiveDot from 'screens/chatbot/components/live-dot';
import EditForm from './edit-form';
import {
  Container,
  LeftPanel,
  RightPanel,
  EmptyState,
  NewButton,
  ListItem,
  ListSummary,
} from './styled-comps';

// Collapse the full DRAFT+PUBLISHED+ARCHIVED list into one row per logical
// bot (grouped by bot_key). Prefer PUBLISHED for the "head" row, fall back to
// DRAFT, and annotate whether a draft is in flight.
function groupByBotKey(rows) {
  const byKey = new Map();
  rows.forEach(b => {
    const key = b.bot_key;
    if (!byKey.has(key)) {
      byKey.set(key, { head: b, hasDraft: false, hasPublished: false });
    }
    const entry = byKey.get(key);
    if (b.status === 'PUBLISHED') {
      entry.head = b;
      entry.hasPublished = true;
    } else if (b.status === 'DRAFT') {
      entry.hasDraft = true;
      if (!entry.hasPublished) entry.head = b;
    }
  });
  return Array.from(byKey.values())
    .map(e => ({ ...e.head, _hasDraft: e.hasDraft, _hasPublished: e.hasPublished }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

function BotsTab() {
  const dispatch = useDispatch();
  const [selectedBot, setSelectedBot] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const {
    get: getBots,
    data: botsData,
    isProcessing: loading,
    error: botsError,
  } = useGetBots();

  const {
    get: getBotDetail,
    data: botDetailData,
    error: botDetailError,
  } = useGetBotDetail();

  const groupedBots = useMemo(
    () => groupByBotKey(Array.isArray(botsData) ? botsData : []),
    [botsData],
  );

  const fetchBots = useCallback(() => {
    invalidateBots();
    getBots('list');
  }, [getBots]);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  useEffect(() => {
    if (botsError) {
      dispatch(
        showNotification({
          text: 'Failed to fetch bots',
          duration: 5,
          showCloseIcon: true,
        }),
      );
    }
  }, [botsError, dispatch]);

  useEffect(() => {
    if (botDetailError) {
      dispatch(
        showNotification({
          text: 'Failed to fetch bot detail',
          duration: 5,
          showCloseIcon: true,
        }),
      );
    }
  }, [botDetailError, dispatch]);

  useEffect(() => {
    if (botDetailData) {
      setSelectedBot(botDetailData);
    }
  }, [botDetailData]);

  const handleSelect = bot => {
    setIsNew(false);
    getBotDetail(bot.id);
  };

  const handleNew = () => {
    setIsNew(true);
    setSelectedBot({
      name: '',
      system_prompt: '',
      kb_scope: '',
      guardrail_rules: [],
      status: 'DRAFT',
    });
  };

  const handleSaved = createdBot => {
    fetchBots();
    // Prefer the id returned from POST (create flow) — selectedBot still holds
    // the local seed object without an id. Falls back to the currently-open
    // row on plain edits.
    const idToFetch = (createdBot && createdBot.id) || (selectedBot && selectedBot.id);
    if (idToFetch) {
      getBotDetail(idToFetch);
    }
    setIsNew(false);
  };

  return (
    <Container>
      <LeftPanel>
        <NewButton label="+ New Bot" onClick={handleNew} />
        {!loading &&
          groupedBots.length > 0 &&
          (() => {
            const live = groupedBots.filter(b => b._hasPublished).length;
            return (
              <ListSummary>
                {groupedBots.length} bot{groupedBots.length === 1 ? '' : 's'} · {live}{' '}
                live
              </ListSummary>
            );
          })()}
        {loading && groupedBots.length === 0 ? (
          <EmptyState>Loading...</EmptyState>
        ) : (
          groupedBots.map(bot => (
            <ListItem
              key={bot.bot_key}
              $selected={!isNew && selectedBot && selectedBot.bot_key === bot.bot_key}
              onClick={() => handleSelect(bot)}
            >
              <LiveDot live={bot._hasPublished} />
              <span className="name">{bot.name}</span>
            </ListItem>
          ))
        )}
        {!loading && !groupedBots.length && <EmptyState>No bots found</EmptyState>}
      </LeftPanel>
      <RightPanel>
        {selectedBot ? (
          <EditForm
            bot={selectedBot}
            onSaved={handleSaved}
            onSelectVersion={handleSelect}
            isNew={isNew}
          />
        ) : (
          <EmptyState>Select a bot from the list</EmptyState>
        )}
      </RightPanel>
    </Container>
  );
}

export default BotsTab;
